import type { FC, PropsWithChildren } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import type { ITableCellProps } from "app/components/React/Table/interfaces/ITableCellProps";
import { translate } from "app/filters/i18nFilter";

import type { IRankSignals } from "../websiteRankings.types";

import { RankChangeValue } from "./RankChangeValue";

interface IChangeWrapperProps extends PropsWithChildren {
    "data-automation": string;
}

const ChangeWrapper = ({ children, ...rest }: IChangeWrapperProps) => (
    <Flex
        justifyContent="flex-end"
        alignItems="center"
        columnGap="2px"
        padding="0 12px 0 0"
        lineHeight="1"
        {...rest}
    >
        {children}
    </Flex>
);

type RankChangeRow = Partial<IRankSignals>;

export const RankChangeCell: FC<ITableCellProps<number | null, RankChangeRow>> = ({ row }) => {
    if (row?.IsNewEntrant) {
        return (
            <ChangeWrapper data-automation="website-rankings-rank-change-new">
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.green["400_P"]}
                    data-automation="website-rankings-rank-change-new-label"
                >
                    {translate("website.rankings.new.entrant.badge").toUpperCase()}
                </Text>
            </ChangeWrapper>
        );
    }

    const rankChange = row?.RankChange;
    if (rankChange === null || rankChange === undefined) {
        return (
            <ChangeWrapper data-automation="website-rankings-rank-change">
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.carbon["300"]}
                >
                    -
                </Text>
            </ChangeWrapper>
        );
    }

    if (rankChange === 0) {
        return (
            <ChangeWrapper data-automation="website-rankings-rank-change">
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.carbon["300"]}
                >
                    0
                </Text>
            </ChangeWrapper>
        );
    }

    return (
        <ChangeWrapper data-automation="website-rankings-rank-change">
            <RankChangeValue value={rankChange} />
        </ChangeWrapper>
    );
};

RankChangeCell.displayName = "RankChangeCell";
